import { useCallback, useMemo } from 'react';
import { Platform } from 'react-native';

import { useLocalFlagsStore } from '@/feature/devsettings/hooks/use-dev-settings';
import { DefaultVertical, mapProductToDefaultVertical, useAuthUserSettings } from '@/hooks/use-auth-user-settings';

import { Product, ProductConfig, getProductType, useJurisdictionStore } from './use-jurisdiction';
import { useUserStore } from './use-user-store';

/**
 * Filters products by availability based on platform and user restrictions.
 * This helper intentionally excludes user preference filtering such as hidden verticals.
 */
export const filterAvailableProducts = (products: ProductConfig[], userEmail?: string): Product[] => {
    return products
        .filter(it => {
            return (
                !it.disabledPlatforms.includes(Platform.OS) && (!userEmail || !it.disabledUsers?.includes(userEmail))
            );
        })
        .map(it => getProductType(it.uid))
        .filter((product, index, array) => product !== Product.None && array.indexOf(product) === index);
};

export const filterHiddenProducts = (products: Product[], hiddenVerticals: DefaultVertical[] = []): Product[] => {
    return products.filter(product => {
        const defaultVertical = mapProductToDefaultVertical(product);
        return !defaultVertical || !hiddenVerticals.includes(defaultVertical);
    });
};

const EMPTY: ProductConfig[] = [];

export const useAvailableProducts = () => {
    const products = useJurisdictionStore(state => state.jurisdictionSettings?.products) ?? EMPTY;
    const activeProduct = useJurisdictionStore.getState().product;
    const profile = useUserStore(state => state.profile);
    const { data: userSettings } = useAuthUserSettings();
    const devEnabledArcadeProduct = useLocalFlagsStore(state => state.localFlags.enableArcadeProduct?.enabled);
    const devEnabledCasinoProduct = useLocalFlagsStore(state => state.localFlags.enableCasinoProduct?.enabled);
    const devEnabledSweepsProduct = useLocalFlagsStore(state => state.localFlags.enableSweepsProduct?.enabled);
    const devEnabledPredictionProduct = useLocalFlagsStore(state => state.localFlags.enablePredictionProduct?.enabled);
    const devEnabledRipsProduct = useLocalFlagsStore(state => state.localFlags.enableRipsProduct?.enabled);

    const availableProducts: Product[] = useMemo(() => {
        let result = filterAvailableProducts(products, profile?.email);

        // Add dev-enabled products that aren't in the API response (filtered due to ineligibility state)
        if (devEnabledArcadeProduct && !result.includes(Product.Arcade)) {
            result = [...result, Product.Arcade];
        }
        if (devEnabledCasinoProduct && !result.includes(Product.Casino)) {
            result = [...result, Product.Casino];
        }
        if (devEnabledSweepsProduct && !result.includes(Product.Sweeps)) {
            result = [...result, Product.Sweeps];
        }
        if (devEnabledPredictionProduct && !result.includes(Product.Prediction)) {
            result = [...result, Product.Prediction];
        }
        if (devEnabledRipsProduct && !result.includes(Product.Rips)) {
            result = [...result, Product.Rips];
        }

        return filterHiddenProducts(result, userSettings?.hidden_verticals);
    }, [
        devEnabledArcadeProduct,
        devEnabledCasinoProduct,
        devEnabledPredictionProduct,
        devEnabledRipsProduct,
        devEnabledSweepsProduct,
        products,
        profile?.email,
        userSettings?.hidden_verticals,
    ]);

    const isProductAvailable = useCallback(
        (product: Product) => {
            return availableProducts.includes(product);
        },
        [availableProducts]
    );

    const isProductActive = useCallback(
        (product: Product) => {
            return product === activeProduct;
        },
        [activeProduct]
    );

    const isProductHiddenByUser = useCallback(
        (product: Product) => {
            const defaultVertical = mapProductToDefaultVertical(product);
            return !!defaultVertical && (userSettings?.hidden_verticals ?? []).includes(defaultVertical);
        },
        [userSettings?.hidden_verticals]
    );

    return { availableProducts, isProductAvailable, isProductActive, isProductHiddenByUser };
};

export type SelectableProduct = Exclude<Product, Product.None>;

export const isSelectableProduct = (product: Product): product is SelectableProduct => product !== Product.None;

/**
 * The available products with the `None` placeholder dropped
 */
export const useSelectableProducts = (): SelectableProduct[] => {
    const { availableProducts } = useAvailableProducts();

    return useMemo(() => availableProducts.filter(isSelectableProduct), [availableProducts]);
};
